import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import FilterBar from '../components/FilterBar.js';
import BlockedOverlay from '../components/BlockedOverlay.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { useFilters } from '../context/FilterContext.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';

import CompetitivenessIndex from '../components/CompetitivenessIndex.js';

const PosTag = ({ diff, inverted = false }) => {
  if (diff === null || diff === undefined) return <span className="text-[10px] text-slate-300">--</span>;
  const isGood = inverted ? diff < 0 : diff > 0;
  const isBad = inverted ? diff > 0 : diff < 0;
  const Icon = isGood ? ArrowUp : isBad ? ArrowDown : Minus;
  const cls = isGood ? 'bg-emerald-100 text-emerald-700' : isBad ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
      <Icon className="w-2.5 h-2.5" />
      {diff > 0 ? '+' : ''}{diff}%
    </span>
  );
};

const StatBox = ({ label, value, unit = '%', p25, p50, p75, myVal, myDiff, inverted = false }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className="text-2xl font-bold text-slate-900">{typeof value === 'number' ? value.toFixed(1) : value}{unit}</p>
    {p25 !== undefined && (
      <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
        <span>1º Q: {p25?.toFixed(1)}</span>
        <span>Med: {p50?.toFixed(1)}</span>
        <span>3º Q: {p75?.toFixed(1)}</span>
      </div>
    )}
    {myVal !== null && myVal !== undefined && (
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <span className="text-[10px] text-slate-500">Sua Empresa: <strong className="text-slate-800">{typeof myVal === 'number' ? myVal.toFixed(1) : myVal}{unit}</strong></span>
        <PosTag diff={myDiff} inverted={inverted} />
      </div>
    )}
  </div>
);

const HRDashboard = () => {
  const [data, setData] = useState(null);
  const { fetchWithFilters, filters } = useFilters();
  const userRole = localStorage.getItem('userRole') || 'respondent';

  useEffect(() => {
    const load = async () => {
      try {
        const result = await fetchWithFilters('/hr/indicators');
        setData(result);
      } catch (e) { console.error(e); }
    };
    load();
  }, [filters, fetchWithFilters]);

  if (!data) return <MainLayout title="Indicadores RH" userRole={userRole}><div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div></MainLayout>;

  const blocked = data.blocked;
  const me = data.minha_empresa;

  const segmentChart = (data.by_segment || []).map(s => ({
    name: s.label.length > 12 ? s.label.slice(0, 12) + '...' : s.label,
    Turnover: s.turnover,
    'Absent.': s.absenteismo,
    PCD: s.pcd,
  }));

  const radarData = [
    { metric: 'Turnover', value: data.turnover_geral?.media || 0 },
    { metric: 'Absent.', value: data.absenteismo?.media || 0 },
    { metric: 'PCD %', value: data.pcd_percentual?.media || 0 },
    { metric: 'Feminino %', value: data.headcount_feminino?.media || 0 },
    { metric: 'Treinamento', value: data.treinamento_horas?.media || 0 },
    { metric: 'Satisf.', value: data.satisfacao?.media || 0 },
  ];

  return (
    <MainLayout title="Indicadores RH" userRole={userRole}>
      <div className="space-y-6" data-testid="hr-dashboard">
        <FilterBar n={data.n} blocked={blocked} />

        {blocked ? <BlockedOverlay n={data.n} /> : (
          <>
            <CompetitivenessIndex variant="compact" />

            {me && (
              <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-lg p-4 text-white" data-testid="hr-company-banner">
                <p className="text-xs opacity-50 mb-1">Posicionamento da sua empresa vs Mercado</p>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-2">
                  {[
                    { l: 'Turnover', v: me.turnover_geral, d: me.turnover_geral_diff_pct, inv: true },
                    { l: 'Absent.', v: me.absenteismo, d: me.absenteismo_diff_pct, inv: true },
                    { l: 'Feminino', v: me.headcount_feminino, d: me.headcount_feminino_diff_pct },
                    { l: 'PCD', v: me.pcd_percentual, d: me.pcd_percentual_diff_pct },
                    { l: 'Treinamento', v: me.treinamento_horas, d: me.treinamento_horas_diff_pct },
                    { l: 'Satisf.', v: me.satisfacao, d: me.satisfacao_diff_pct },
                  ].filter(i => i.v !== null).map((item, i) => (
                    <div key={i} className="bg-white/10 rounded-lg p-2.5">
                      <p className="text-[10px] opacity-60">{item.l}</p>
                      <p className="text-lg font-bold">{item.v?.toFixed(1)}</p>
                      {item.d !== null && <PosTag diff={item.d} inverted={item.inv} />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatBox label="Turnover Geral" value={data.turnover_geral?.media} p25={data.turnover_geral?.p25} p50={data.turnover_geral?.p50} p75={data.turnover_geral?.p75} myVal={me?.turnover_geral} myDiff={me?.turnover_geral_diff_pct} inverted />
              <StatBox label="Turnover Volunt." value={data.turnover_voluntario?.media} p25={data.turnover_voluntario?.p25} p50={data.turnover_voluntario?.p50} p75={data.turnover_voluntario?.p75} myVal={me?.turnover_voluntario} myDiff={me?.turnover_voluntario_diff_pct} inverted />
              <StatBox label="Absent." value={data.absenteismo?.media} p25={data.absenteismo?.p25} p50={data.absenteismo?.p50} p75={data.absenteismo?.p75} myVal={me?.absenteismo} myDiff={me?.absenteismo_diff_pct} inverted />
              <StatBox label="PCD" value={data.pcd_percentual?.media} p25={data.pcd_percentual?.p25} p50={data.pcd_percentual?.p50} p75={data.pcd_percentual?.p75} myVal={me?.pcd_percentual} myDiff={me?.pcd_percentual_diff_pct} />
              <StatBox label="Satisf." value={data.satisfacao?.media} p25={data.satisfacao?.p25} p50={data.satisfacao?.p50} p75={data.satisfacao?.p75} myVal={me?.satisfacao} myDiff={me?.satisfacao_diff_pct} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatBox label="Tempo Recrutamento" value={data.tempo_medio_recrutamento?.media} unit=" dias" myVal={me?.tempo_medio_recrutamento} myDiff={me?.tempo_medio_recrutamento_diff_pct} inverted />
              <StatBox label="Custo Contrat." value={`R$ ${(data.custo_medio_contratacao?.media || 0).toLocaleString('pt-BR', {minimumFractionDigits: 0})}`} unit="" />
              <StatBox label="Treinamento" value={data.treinamento_horas?.media} unit=" h/capita" myVal={me?.treinamento_horas} myDiff={me?.treinamento_horas_diff_pct} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Indicadores por Segmento</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={segmentChart} margin={{ top: 5, right: 20, bottom: 60, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="Turnover" fill="#2563eb" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Absent." fill="#0f172a" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="PCD" fill="#64748b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Visão Geral</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis tick={{ fontSize: 10 }} />
                      <Radar name="Mercado" dataKey="value" stroke="#2563eb" fill="#2563eb" fillOpacity={0.2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default HRDashboard;
